import { X, CheckIcon } from 'lucide-react-native';
import { Pressable } from 'react-native';

import { MultiSelectActionsheet } from '@/components/MultiSelectActionsheet';
import { ActionsheetItem } from '@/components/ui/actionsheet';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonIcon } from '@/components/ui/button';
import {
  CheckboxGroup,
  Checkbox,
  CheckboxIndicator,
  CheckboxIcon,
  CheckboxLabel,
} from '@/components/ui/checkbox';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { useMultiSelect } from '@/hooks/useMultiSelect';
import { getNativeTextProps } from '@/util/getNativeTextProps';

export interface SubspecialtyOption {
  id: number;
  label: string;
}

interface Props {
  value: number[];
  options: SubspecialtyOption[];
  onChange: (val: number[]) => void;
  placeholder?: string;
}

export function SubspecialtyMultiSelect({
  value,
  options,
  onChange,
  placeholder = 'Select subspecialties',
}: Props) {
  const {
    isOpen,
    setIsOpen,
    selectedOptions,
    handleSelectionChange,
    handleRemoveItem,
    isItemDisabled,
  } = useMultiSelect({
    value,
    options,
    onChange,
    maxItems: 5,
    getId: (item) => item.id,
  });

  return (
    <>
      {/* Trigger */}
      <Pressable
        onPress={() => setIsOpen(true)}
        className="min-h-9 flex-row flex-wrap items-center rounded-md border border-outline-300 bg-background-0 px-3 py-2">
        {selectedOptions.length ? (
          <HStack className="flex-wrap items-center gap-2">
            {selectedOptions.map((opt) => (
              <Badge key={opt.id} action="info" size="sm" className="border py-0">
                <HStack className="items-center gap-1">
                  <BadgeText className="max-w-32 text-xs capitalize" {...getNativeTextProps()}>
                    {opt.label}
                  </BadgeText>
                  <Button
                    variant="link"
                    size="xs"
                    onPress={() => handleRemoveItem(opt.id)}
                    accessibilityLabel={`Remove ${opt.label}`}>
                    <ButtonIcon as={X} size="xs" className="text-info-800" />
                  </Button>
                </HStack>
              </Badge>
            ))}
          </HStack>
        ) : (
          <Text className="text-sm text-typography-400">{placeholder}</Text>
        )}
      </Pressable>

      {/* Actionsheet */}
      <MultiSelectActionsheet
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Select up to 5">
        <CheckboxGroup value={value.map(String)} onChange={handleSelectionChange}>
          {options.map((opt) => (
            <ActionsheetItem
              key={opt.id}
              className="p-0 data-[hover=false]:bg-transparent data-[active=true]:bg-transparent">
              <Checkbox
                value={String(opt.id)}
                size="md"
                className="w-full flex-row items-center p-3 focus:bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-background-100"
                isDisabled={isItemDisabled(opt)}>
                <CheckboxIndicator>
                  <CheckboxIcon as={CheckIcon} />
                </CheckboxIndicator>
                <CheckboxLabel className="tracking-md ml-2 flex-1 text-left font-body font-normal text-typography-700">
                  {opt.label}
                </CheckboxLabel>
              </Checkbox>
            </ActionsheetItem>
          ))}
        </CheckboxGroup>
      </MultiSelectActionsheet>
    </>
  );
}
